import React, { useContext, useEffect, useRef, useState } from 'react'
import { t } from '../i18n/i18n'
import { RiskAnalysis, RiskAnalysisType } from '../api/schemas/schema'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../helpers/apiHelper'
import { handleMessage } from '../helpers/stateHelper'
import { Loader } from '../components/common/spinner/Loader'
import { DataContext } from '../context/DataContext'
import { SWOTAnalysis } from '../components/riskAnalyses/SWOT/SWOTAnalysis'
import { SimplifiedRiskAnalysis } from '../components/riskAnalyses/Risks/SimplifiedRiskAnalysis/SimplifiedRiskAnalysis'
import { IItemState } from '../interfaces/IItemState'
import { NavigationLink } from '../interfaces/INavigationLink'
import { isRiskAnalysisOwnerLeaderOrCreator } from '../helpers/riskManagementHelper'
import { isSystemAdmin } from '../helpers/permissionHelper'
import { ConsequenceAndProbabilityAnalysis } from '../components/riskAnalyses/Risks/ConsequenceAndProbabilityAnalysis/ConsequenceAndProbabilityAnalysis'

export const SelectedRiskAnalysis = () => {
   const dataContext = useContext(DataContext)
   const { userAccount } = dataContext.state
   const navigate = useNavigate()
   const [riskAnalysisState, setRiskAnalysisState] = useState<IItemState<RiskAnalysis>>({
      loading: true,
      error: null,
      item: null,
      disabled: false,
   })
   const { item, loading, error } = riskAnalysisState
   const { id } = useParams()
   const prevId = useRef({})

   useEffect(() => {
      const fetchData = async () => {
         try {
            const riskAnalysis = (await api().getRiskAnalysisById(id, dataContext.state.organisation.id)).data
            if (!riskAnalysis) {
               navigate(NavigationLink.RiskManagement)
               handleMessage(dataContext.setRootState, 'error', t('FetchFailed'), t('CouldNotFetchItem', { Item: t('RiskAnalysis') }))
            }

            setRiskAnalysisState((prev) => ({ ...prev, loading: false, item: riskAnalysis }))
         } catch (error: any) {
            setRiskAnalysisState((prev) => ({
               ...prev,
               loading: false,
               error: { message: error.message ? error.message : error.error.message, name: error.status },
            }))

            navigate(NavigationLink.RiskManagement)
            handleMessage(dataContext.setRootState, 'error', t('FetchFailed'), t('CouldNotFetchItem', { Item: t('RiskAnalysis') }))
         }
      }
      if (prevId.current !== id) {
         prevId.current = id
         fetchData()
      }

      if (riskAnalysisState.item && riskAnalysisState.item.organisationId !== dataContext.state.organisation.id) {
         navigate(NavigationLink.RiskManagement)
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id, dataContext.state.userAccount])

   useEffect(() => {
      if (riskAnalysisState?.item) {
         if (!isRiskAnalysisOwnerLeaderOrCreator(riskAnalysisState.item, userAccount) && !isSystemAdmin(userAccount)) {
            setRiskAnalysisState((prev) => ({ ...prev, disabled: true }))
         }
      }
   }, [riskAnalysisState, userAccount])

   const getContent = (type: RiskAnalysisType): JSX.Element => {
      switch (type) {
         case RiskAnalysisType.SWOT:
            return <SWOTAnalysis analysisState={riskAnalysisState} setRiskAnalysisState={setRiskAnalysisState} />
         case RiskAnalysisType.SimplifiedRiskAnalysis:
            return <SimplifiedRiskAnalysis analysis={item} analysisState={riskAnalysisState} setRiskAnalysisState={setRiskAnalysisState} />
         case RiskAnalysisType.ConsequenceAndProbabilityAnalysis:
            return <ConsequenceAndProbabilityAnalysis analysis={item} analysisState={riskAnalysisState} setRiskAnalysisState={setRiskAnalysisState} />
         default:
            return <></>
      }
   }
   return <>{loading ? <Loader /> : error ? <>{t('CouldNotFetchItem', { Item: t('RiskAnalysis').toLowerCase() })}</> : getContent(item.type)}</>
}
